// Online Payments: a traffic fine or a licence renewal paid online by card.
// db/migrations/8002.
//
// The fines people (cockpit_access.can_manage_fines: Musa, Kabelo, Thabang)
// raise it, Luyanda approves it, and the person with the card pays it and files
// the proof. The other way round from Petrol Requests, where Luyanda pays: here
// he only approves. "Paid" stays shut until a proof of payment is filed,
// because the proof is the only record that the fine is settled.
//
// Who carries a fine is not decided here. That is the Traffic Fines tab's work.
// Every figure and refusal on this page is Postgres's. CLAUDE.md rule 4.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Check, ChevronDown, ChevronRight, CreditCard, X } from 'lucide-react'
import { useDecideOnlinePayment, useGetOnlinePayments, useRaiseOnlinePayment } from '../hooks/backend/onlinePayments'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Chip } from '../components/interviews/Chip'
import { PaymentFiles, type PaymentFile } from '../components/payments/PaymentFiles'
import { ErrorNote, PageGate, REQUEST_TONE, Row, STATUS_FILTERS, type RequestStatus } from '../components/payments/requestParts'
import { formatDay, formatWhen, formatZar } from '../utils/loans'

type Kind = 'traffic_fine' | 'licence_renewal' | 'other'

type OnlinePayment = {
  id: string; ref: string; kind: Kind; kind_text: string; vehicle: string | null; driver_name: string | null
  reference: string | null; pay_where: string; amount_zar: number; due_date: string | null; notes: string | null
  status: RequestStatus; requested_by: string; requested_by_name: string | null; requested_at: string
  approved_at: string | null; approved_by_name: string | null
  declined_at: string | null; decline_reason: string | null
  paid_at: string | null; paid_by_name: string | null; paid_note: string | null
  cancelled_at: string | null; cancel_reason: string | null
  has_proof: boolean; files: PaymentFile[]
}

type OnlineList = {
  can_raise: boolean; can_approve: boolean; approvers: string; today: string
  vehicles: { id: string; label: string }[] | null
  drivers: { id: string; name: string }[] | null
  rows: OnlinePayment[]
  counts: { to_approve: number; to_pay: number; paid_this_month: number; paid_this_month_zar: number }
}

const STATUS_LABEL: Record<RequestStatus, string> = {
  requested: 'Waiting for approval',
  approved: 'Approved, pay it now',
  paid: 'Paid',
  declined: 'Declined',
  cancelled: 'Cancelled',
}

export default function OnlinePayments() {
  const list = useGetOnlinePayments<OnlineList | null>()
  const [params] = useSearchParams()
  const deepLink = params.get('r')
  const [status, setStatus] = useState(deepLink ? '' : 'open')
  const [expanded, setExpanded] = useState<string | null>(deepLink)
  const [raising, setRaising] = useState(false)

  // The hook starts at data null, which is also Postgres's answer for a login
  // that may not see this page, so the first answer is waited for explicitly.
  const [settled, setSettled] = useState(false)
  const load = list.trigger
  const refresh = useCallback(() => {
    void load({ status: status || null }, { skipCache: true }).catch(() => undefined).finally(() => setSettled(true))
  }, [load, status])
  useEffect(() => { refresh() }, [refresh])

  const data = list.data ?? null
  const approvers = data?.approvers ?? 'Luyanda'

  return (
    <PageGate
      loading={!settled}
      error={data === null ? list.error : null}
      denied={settled && data === null && !list.error && !list.loading}
      onRetry={refresh}
      deniedTitle="Online payments are open to the people who handle fines and to Luyanda."
      deniedHint="If you should be one of them, ask an admin to switch on Manage fines for you in cockpit access."
    >
      <div className="flex h-full min-h-0 flex-col bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <CreditCard className="h-5 w-5" />
          </div>
          <div className="min-w-[12rem] flex-1">
            <h1 className="text-lg font-semibold text-foreground">Online Payments</h1>
            <p className="text-xs text-muted-foreground">
              Traffic fines and licence renewals paid online by card. {approvers} approves first; then you pay it and file the proof of payment here.
            </p>
          </div>
          {data?.can_raise && (
            <Button variant="brand" size="sm" onClick={() => setRaising(true)}>
              <CreditCard className="mr-1 h-4 w-4" /> Ask to pay something
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          {data?.counts && (
            <>
              <Chip tone={data.counts.to_approve ? 'amber' : 'neutral'}>{data.counts.to_approve} waiting for approval</Chip>
              <Chip tone={data.counts.to_pay ? 'brand' : 'neutral'}>{data.counts.to_pay} approved, to pay</Chip>
              <Chip tone="neutral">{data.counts.paid_this_month} paid this month, {formatZar(data.counts.paid_this_month_zar)}</Chip>
            </>
          )}
          <NativeSelect className="ml-auto h-8 w-44 text-xs" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            {STATUS_FILTERS('Approved, to pay').map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </NativeSelect>
        </div>

        {list.error && data !== null && <div className="border-b border-border px-4 py-2"><ErrorNote>{list.error}</ErrorNote></div>}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {(data?.rows ?? []).length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {status === 'open' ? 'Nothing is waiting. Ask to pay a fine or a licence with the button above.' : 'No payment request matches this filter.'}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {(data?.rows ?? []).map((r) => (
                <OnlineItem key={r.id} req={r} open={expanded === r.id}
                  onToggle={() => setExpanded((x) => (x === r.id ? null : r.id))}
                  canApprove={data?.can_approve === true} canPay={data?.can_raise === true || data?.can_approve === true}
                  approvers={approvers} onChanged={refresh} />
              ))}
            </ul>
          )}
        </div>

        <RaiseOnlinePaymentDialog open={raising} onOpenChange={setRaising} data={data} onSaved={refresh} />
      </div>
    </PageGate>
  )
}

function OnlineItem({ req: r, open, onToggle, canApprove, canPay, approvers, onChanged }: {
  req: OnlinePayment; open: boolean; onToggle: () => void; canApprove: boolean; canPay: boolean
  approvers: string; onChanged: () => void
}) {
  const decide = useDecideOnlinePayment()
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [asking, setAsking] = useState<'decline' | 'cancel' | null>(null)
  const [why, setWhy] = useState('')
  const unpaid = r.status === 'requested' || r.status === 'approved'

  async function act(action: 'approve' | 'decline' | 'paid' | 'cancel', text = '') {
    setError(null)
    try {
      await decide.trigger({ id: r.id, action, text })
      setAsking(null); setWhy('')
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <li>
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-accent/40">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{r.ref}</span>
        <span className="min-w-[10rem] flex-1">
          <span className="block text-sm font-medium text-foreground">{r.kind_text}{r.vehicle ? `, ${r.vehicle}` : ''}</span>
          <span className="block text-xs text-muted-foreground">{r.pay_where}{r.reference ? `, ${r.reference}` : ''}{r.due_date ? `, due ${formatDay(r.due_date)}` : ''}</span>
        </span>
        <span className="w-24 text-right text-sm font-semibold tabular-nums text-foreground">{formatZar(r.amount_zar)}</span>
        <span className="flex w-full justify-end sm:w-auto"><Chip tone={REQUEST_TONE[r.status]}>{STATUS_LABEL[r.status]}</Chip></span>
      </button>

      {open && (
        <div className="space-y-3 bg-muted/20 px-4 pb-4 pl-11 text-sm">
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <Row k="What" v={r.kind_text} />
            <Row k="Car" v={r.vehicle ?? '—'} />
            <Row k="Reference" v={r.reference ?? '—'} />
            <Row k="Paid on" v={r.pay_where} />
            <Row k="Due" v={r.due_date ? formatDay(r.due_date) : '—'} />
            {r.driver_name && <Row k="Driver" v={r.driver_name} />}
            {r.notes && <Row k="Notes" v={r.notes} />}
            <Row k="Raised by" v={`${r.requested_by_name ?? '—'}, ${formatWhen(r.requested_at)}`} />
            {r.approved_at && <Row k="Approved" v={`${formatWhen(r.approved_at)}${r.approved_by_name ? ` by ${r.approved_by_name}` : ''}`} />}
            {r.paid_at && <Row k="Paid" v={`${formatWhen(r.paid_at)} by ${r.paid_by_name ?? '—'}${r.paid_note ? `: ${r.paid_note}` : ''}`} />}
            {r.declined_at && <Row k="Declined" v={`${formatWhen(r.declined_at)}: ${r.decline_reason ?? ''}`} />}
            {r.cancelled_at && <Row k="Cancelled" v={`${formatWhen(r.cancelled_at)}: ${r.cancel_reason ?? ''}`} />}
          </dl>

          {r.status === 'requested' && canApprove && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] p-3 text-xs text-[hsl(var(--brand-soft-foreground))]">
              <span className="text-sm font-semibold">Approve {formatZar(r.amount_zar)} on {r.pay_where}?</span>
              <Button size="sm" variant="brand" disabled={decide.loading} onClick={() => void act('approve')}>
                {decide.loading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Check className="mr-1 h-3.5 w-3.5" />} Approve
              </Button>
              {asking !== 'decline' && <Button size="sm" variant="ghost" onClick={() => setAsking('decline')}>Decline</Button>}
            </div>
          )}
          {r.status === 'requested' && !canApprove && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              Waiting for {approvers} to approve it. Do not pay it yet.
            </p>
          )}
          {r.status === 'approved' && canPay && (
            <div className="space-y-2 rounded-md border border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] p-3 text-xs text-[hsl(var(--brand-soft-foreground))]">
              <p className="text-sm font-semibold">Approved. Pay {formatZar(r.amount_zar)} on {r.pay_where}{r.reference ? ` for ${r.reference}` : ''} with your card.</p>
              <p>{r.has_proof ? 'The proof of payment is filed. Press Paid to close it.' : 'Then upload the proof of payment below. Paid opens once it is filed.'}</p>
              <span className="flex flex-wrap items-center gap-1">
                <Input className="h-8 w-60 bg-background text-xs" placeholder="Optional note, e.g. paid on the FNB card" value={note} onChange={(e) => setNote(e.target.value)} />
                <Button size="sm" variant="brand" disabled={decide.loading || !r.has_proof} onClick={() => void act('paid', note)}>
                  <Check className="mr-1 h-3.5 w-3.5" /> Paid
                </Button>
                {canApprove && asking !== 'decline' && <Button size="sm" variant="ghost" onClick={() => setAsking('decline')}>Decline</Button>}
              </span>
            </div>
          )}

          <div className="space-y-1">
            <span className="text-xs font-medium text-foreground">Files</span>
            {r.files.length === 0 && <p className="text-xs text-muted-foreground">No files yet.</p>}
            <PaymentFiles kind="online" requestId={r.id} files={r.files} onChanged={onChanged}
              uploadRoles={canPay && r.status !== 'declined' && r.status !== 'cancelled' ? [
                { role: 'evidence', label: 'Upload the fine or licence notice' },
                ...(r.status === 'approved' || r.status === 'paid' ? [{ role: 'proof' as const, label: 'Upload proof of payment' }] : []),
              ] : []} />
          </div>

          {unpaid && asking === null && (
            <Button size="sm" variant="ghost" onClick={() => setAsking('cancel')}>Cancel this request</Button>
          )}
          {asking && (
            <span className="flex flex-wrap items-center gap-1">
              <Input className="h-8 w-64 text-xs" placeholder={asking === 'decline' ? 'Why it is declined (they read this)' : 'Why it is being cancelled'} value={why} onChange={(e) => setWhy(e.target.value)} />
              <Button size="sm" variant="destructive" disabled={decide.loading} onClick={() => void act(asking, why)}>
                {asking === 'decline' ? 'Decline it' : 'Cancel it'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAsking(null)}><X className="h-3.5 w-3.5" /></Button>
            </span>
          )}
          {error && <ErrorNote>{error}</ErrorNote>}
        </div>
      )}
    </li>
  )
}

function RaiseOnlinePaymentDialog({ open, onOpenChange, data, onSaved }: {
  open: boolean; onOpenChange: (o: boolean) => void; data: OnlineList | null; onSaved: () => void
}) {
  const raise = useRaiseOnlinePayment<{ ok: boolean; id: string; ref: string }>()
  const [kind, setKind] = useState<Kind>('traffic_fine')
  const [vehicleId, setVehicleId] = useState('')
  const [driverId, setDriverId] = useState('')
  const [reference, setReference] = useState('')
  const [payWhere, setPayWhere] = useState('')
  const [amount, setAmount] = useState('')
  const [due, setDue] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setKind('traffic_fine'); setVehicleId(''); setDriverId(''); setReference(''); setPayWhere('PayCity')
    setAmount(''); setDue(''); setNotes(''); setError(null); setSaved(null)
  }, [open])

  function chooseKind(k: Kind) {
    setKind(k)
    // A default, never a rule: where it is paid is whatever the person types.
    setPayWhere(k === 'traffic_fine' ? 'PayCity' : k === 'licence_renewal' ? 'NaTIS online' : '')
  }

  async function save() {
    setError(null)
    try {
      const r = await raise.trigger({
        kind, vehicle_id: vehicleId || null, driver_id: driverId || null, reference, pay_where: payWhere,
        amount_zar: amount.replace(',', '.').trim(), due_date: due || null, notes,
      })
      if (r) { setSaved(r.ref); onSaved() }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const approvers = data?.approvers ?? 'Luyanda'

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!raise.loading) onOpenChange(o) }}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        {saved ? (
          <>
            <DialogHeader>
              <DialogTitle>{saved} is with {approvers}</DialogTitle>
              <DialogDescription>{approvers} has been emailed to approve it. You will be emailed when it is approved; pay it then, not before, and file the proof of payment here.</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end"><Button variant="brand" onClick={() => onOpenChange(false)}>Done</Button></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Ask to pay something online</DialogTitle>
              <DialogDescription>{approvers} approves it, then you pay it with your card and file the proof of payment.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-4 text-xs">
                {([['traffic_fine', 'Traffic fine'], ['licence_renewal', 'Licence renewal'], ['other', 'Something else']] as [Kind, string][]).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2"><input type="radio" name="op-kind" checked={kind === k} onChange={() => chooseKind(k)} /> {label}</label>
                ))}
              </div>
              <div className="space-y-1">
                <label htmlFor="op-vehicle" className="text-xs font-medium text-foreground">Car{kind === 'other' ? ' (optional)' : ''}</label>
                <NativeSelect id="op-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                  <option value="">Choose the car</option>
                  {(data?.vehicles ?? []).map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                </NativeSelect>
              </div>
              {kind === 'traffic_fine' && (
                <div className="space-y-1">
                  <label htmlFor="op-driver" className="text-xs font-medium text-foreground">Driver at the time (optional)</label>
                  <NativeSelect id="op-driver" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
                    <option value="">Not known, or not a driver</option>
                    {(data?.drivers ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </NativeSelect>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label htmlFor="op-ref" className="text-xs font-medium text-foreground">{kind === 'traffic_fine' ? 'Notice number' : 'Reference (optional)'}</label>
                  <Input id="op-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="op-where" className="text-xs font-medium text-foreground">Paid on</label>
                  <Input id="op-where" placeholder="PayCity, NaTIS online, ..." value={payWhere} onChange={(e) => setPayWhere(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="op-amount" className="text-xs font-medium text-foreground">Amount (R)</label>
                  <Input id="op-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="op-due" className="text-xs font-medium text-foreground">Due date (optional)</label>
                  <Input id="op-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="op-notes" className="text-xs font-medium text-foreground">Notes (optional)</label>
                <Input id="op-notes" placeholder="e.g. discount if paid before the due date" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              {error && <ErrorNote>{error}</ErrorNote>}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={raise.loading}>Cancel</Button>
                <Button variant="brand" onClick={() => void save()} disabled={raise.loading}>
                  {raise.loading ? <Spinner className="mr-1 h-4 w-4" /> : <CreditCard className="mr-1 h-4 w-4" />} Send to {approvers}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
